import { Stack, Group, Text, ActionIcon } from '@mantine/core';
import { IconMinus, IconPlus } from '@tabler/icons-react';

type CountStepperProps = {
  label: string;
  description?: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
};

/**
 * Touch-friendly counter for small integer ranges. Uses +/- buttons instead of a
 * free-text NumberInput so mobile users can't accidentally append digits to a
 * pre-filled default (e.g. typing "4" into "2" producing "24").
 */
export const CountStepper = ({
  label,
  description,
  value,
  onChange,
  min = 0,
  max = 10,
}: CountStepperProps) => {
  const clamp = (n: number) => Math.min(max, Math.max(min, n));
  const decrement = () => onChange(clamp(value - 1));
  const increment = () => onChange(clamp(value + 1));

  return (
    <Stack gap={4}>
      <Text fw={500} size="sm">
        {label}
      </Text>
      {description && (
        <Text size="xs" c="dimmed">
          {description}
        </Text>
      )}
      <Group
        gap="md"
        justify="space-between"
        wrap="nowrap"
        mt={4}
        p={6}
        style={{
          border: '1px solid var(--mantine-color-gray-3)',
          borderRadius: 'var(--mantine-radius-md)',
          background: 'var(--mantine-color-white)',
        }}
      >
        <ActionIcon
          size={44}
          radius="md"
          variant="light"
          color="orange"
          onClick={decrement}
          disabled={value <= min}
          aria-label={`Decrease ${label.toLowerCase()}`}
        >
          <IconMinus size={20} />
        </ActionIcon>

        <Text fw={700} size="xl" style={{ minWidth: 32, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
          {value}
        </Text>

        <ActionIcon
          size={44}
          radius="md"
          variant="light"
          color="orange"
          onClick={increment}
          disabled={value >= max}
          aria-label={`Increase ${label.toLowerCase()}`}
        >
          <IconPlus size={20} />
        </ActionIcon>
      </Group>
    </Stack>
  );
};
